import { Dropzone } from '@zorro/design-system/v2/Dropzone';
import { GenericFileIcon } from '@zorro/shared/assets';
import { Box, InputWrapper, Stack, Text } from '@zorro/web/design-system';
import { showErrorNotification } from '@zorro/web/ui-utils';
import { ReactNode } from 'react';
import { Control, Controller } from 'react-hook-form';

export type FileUploaderProps = {
  control: Control;
  name: string;
  label: string;
  topRightContent?: ReactNode;
  accept?: string[];
  isLoading?: boolean;
  children?: ReactNode;
  onDrop: (
    files: File[],
    onDrop: () => void,
    onChange: (file: File[] | File) => void
  ) => void;
  isRequired?: boolean;
  isMultiple?: boolean;
  isDisabled?: boolean;
};

export const FileUploader = ({
  control,
  name,
  label,
  accept,
  isLoading,
  topRightContent,
  children,
  onDrop,
  isMultiple = false,
  isDisabled = false,
  isRequired = false,
  buttonText,
  zoneLabelText,
}: FileUploaderProps & {
  buttonText: string;
  zoneLabelText: string;
}) => {
  'use no memo';
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { ref: _ref, onBlur, onChange, ...rest } }) => (
        <InputWrapper pos="relative" label={label} required={isRequired}>
          <Box pos="absolute" top={0} right={0}>
            {topRightContent}
          </Box>
          <Dropzone
            {...rest}
            accept={accept}
            multiple={isMultiple}
            onDrop={(files: File[]) => {
              onDrop(files, onBlur, onChange);
            }}
            onReject={(files) => {
              showErrorNotification({
                message: files[0].errors[0].message,
                title: files[0].errors[0].code,
              });
            }}
            loading={isLoading}
            disabled={isDisabled}
          >
            <Stack align="center" gap="xs" w="100%">
              {children ?? <GenericFileIcon />}
              {buttonText && (
                <Text fw={600} size="sm">
                  {buttonText}
                </Text>
              )}
              {zoneLabelText && (
                <Text fw={400} c="zorroGray.4" size="xs">
                  {zoneLabelText}
                </Text>
              )}
            </Stack>
          </Dropzone>
        </InputWrapper>
      )}
    />
  );
};
